import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { CardContent, CardMedia, Skeleton, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Container } from "@mui/system";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CardComp from "../../components/CardComp";
import TablePreloader from "../../components/Loader/TablePreloader";
import Table from "../../components/table/Table";
import { getHmoData, getHmos } from "../../features/hmo/hmoSlice";
import {
  getHospitalData,
  getHospitals,
} from "../../features/hospitals/hospitalSlice";
import {
  getSuperAdminDashboard,
  getUserData,
} from "../../features/users/userSlice";
import HomeLayout from "../../layouts/Home";
import config from "../../utils/config";
import { capitalize, getAmount } from "../../utils/helper";
import {
  IArrowFoward,
  IBoxes,
  IClientTwo,
  IHmoTwo,
  IHomeTwo,
} from "../../utils/icons.utils";

export function Loader({ elementCount }) {
  return Array(elementCount)
    .fill("")
    .map((a, i) => {
      return (
        <Box key={i} className="w-full">
          <CardContent className="flex gap-6">
            <Skeleton
              className={`w-full h-[106px] rounded-lg`}
              animation="wave"
              variant="rectangular"
            ></Skeleton>
          </CardContent>
        </Box>
      );
    });
}
const { routes } = config;
export default function Dashboard() {
  const { hospitals, pagination, loading } = useSelector(getHospitalData),
    {
      hmos,
      loading: hmoLoading,
      pagination: hmoPagination,
    } = useSelector(getHmoData),
    { stats, loading: statsLoading } = useSelector(getUserData),
    dispatch = useDispatch();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(getHospitals({ pageSize: 5 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dispatch(getHmos({ pageSize: 5 }));
    dispatch(getSuperAdminDashboard());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let hospitalData = hospitals?.map((hospital) => ({
    "Hospital Name": capitalize(hospital?.name),
    email: hospital?.email,
    "Phone Number": hospital?.phoneNumber,
    "RC Number": hospital?.rcNumber,
    // address: hospital?.streetName,
    // "Created Date": dayjs(hospital?.createdAt).format("MMM DD, YYYY"),
    _data: hospital,
  }));

  let hmoData = hmos?.map((hmo) => ({
    "HMO Name": capitalize(hmo?.name),
    address: capitalize(hmo?.streetName),
    "Contact Name": hmo?.user?.firstName + " " + hmo?.user?.lastName,
  }));

  const mapData = useMemo(() => {
    return [
      {
        icon: IHomeTwo,
        title: statsLoading ? "..." : stats?.registeredHospitals,
        subtitle: "Registered Hospitals",
      },
      {
        icon: IBoxes,
        title: statsLoading ? "..." : "NGN " + getAmount(stats?.roi || 0),
        subtitle: "Generated R. 0. I ",
      },
      {
        icon: IHmoTwo,
        title: statsLoading ? "..." : stats?.registeredHmos,
        subtitle: "Registered H. M. O",
      },
    ];
  }, [stats, statsLoading]);

  const mapDataTwo = useMemo(() => {
    return [
      {
        icon: IHomeTwo,
        title: statsLoading ? "..." : stats?.individualClients,
        subtitle: "Indiviual Clients",
        iconTwo: IArrowFoward,
      },
      {
        icon: IClientTwo,
        title: statsLoading ? "..." : stats?.companyClients,
        subtitle: "Company Clients  ",
        iconTwo: IArrowFoward,
      },
    ];
  }, [stats, statsLoading]);

  return (
    <HomeLayout
      {...{
        title: "Dashboard",
        subtitle: "Overview of all information on the system.",
      }}
    >
      <div className="mb-10" data-cy="super-admin-dashboard-page">
        <Typography
          variant="h6"
          noWrap
          component="div"
          color="#040316"
          sx={{ my: 3 }}
        >
          Business Overview
        </Typography>
        <Box
          className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4 lg:gap-10 p-4 mb-8 max-w-full"
          data-cy="dashboard-kpi-section"
        >
          {statsLoading ? (
            <Loader elementCount={3} />
          ) : (
            mapData.map((data, index) => {
              return (
                <Box
                  elevation={3}
                  key={index}
                  data-cy={
                    index === 0
                      ? "dashboard-registered-hospitals"
                      : index === 1
                      ? "dashboard-generated-roi"
                      : "dashboard-registered-hmos"
                  }
                >
                  <CardComp
                    className={
                      "bg-[var(--c-bg-color)] w-[100%] pt-2 xl:pl-3 border-none shadow-md rounded-xl"
                    }
                  >
                    <CardContent className="flex gap-6 ">
                      <Box
                        className={`  rounded-lg`}
                        sx={{ height: 60, width: 60 }}
                      >
                        <CardMedia
                          component="img"
                          className={`${
                            index === 2 && "xl:p-4 lg:p-3 md:p-2 p-4"
                          } ${index === 2 && "bg-[#007AFF1A] rounded-lg"}`}
                          image={data.icon}
                          alt="Paella dish"
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{ fontSize: 20, fontWeight: 700 }}
                          variant="h1"
                          color="black"
                          gutterBottom
                          fontFamily={"Lota"}
                        >
                          {data.title}
                        </Typography>
                        <Typography
                          sx={{ mb: 1.5 }}
                          color="text.secondary"
                          fontFamily={"Lota"}
                        >
                          {data.subtitle}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardComp>
                </Box>
              );
            })
          )}
        </Box>

        <Typography
          variant="h6"
          noWrap
          component="div"
          color="#040316"
          sx={{ my: 3 }}
          fontFamily={"Lota"}
        >
          Registered Clients
        </Typography>
        <Box
          className="grid lg:grid-cols-2 md:grid-cols-1 grid-cols-1 gap-4 lg:gap-10 p-4 mb-14"
          data-cy="dashboard-registered-clients-section"
        >
          {statsLoading ? (
            <Loader elementCount={2} />
          ) : (
            mapDataTwo.map((data, index) => {
              return (
                <Box
                  elevation={3}
                  key={index}
                  data-cy={
                    index === 0
                      ? "dashboard-individual-clients"
                      : "dashboard-company-clients"
                  }
                >
                  <CardComp
                    className={
                      "bg-[var(--c-bg-color)] w-[100%] pt-2 xl:pl-3 border-none shadow-md rounded-xl"
                    }
                  >
                    <CardContent className="flex gap-6 justify-between">
                      <Box className="flex gap-6">
                        <Box
                          className={`  rounded-lg`}
                          sx={{ height: 60, width: 60 }}
                        >
                          <CardMedia
                            component="img"
                            className={`${
                              index === 2 &&
                              "xl:p-4 lg:p-3 md:p-2 p-4 rounded-lg"
                            } ${index === 2 && "bg-[#007AFF1A]"}`}
                            image={data.icon}
                            alt="Paella dish"
                          />
                        </Box>
                        <Box>
                          <Typography
                            sx={{ fontSize: 20, fontWeight: 700 }}
                            variant="h1"
                            color="black"
                            gutterBottom
                            fontFamily={"Lota"}
                          >
                            {data.title}
                          </Typography>
                          <Typography
                            sx={{ mb: 1.5 }}
                            color="text.secondary"
                            fontFamily={"Lota"}
                          >
                            {data.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                      <CardMedia
                        component="img"
                        className={`w-[18px] h-[15px] mt-12 cursor-pointer`}
                        image={data.iconTwo}
                        alt="Paella dish"
                      />
                    </CardContent>
                  </CardComp>
                </Box>
              );
            })
          )}
        </Box>

        <Box className="grid lg:grid-cols-2 gap-4">
          <div className="w-full">
            <Container
              className="py-5 bg-[var(--c-bg-color)] rounded-lg"
              sx={{ px: 0 }}
              data-cy="dashboard-top-hospitals"
            >
              <Box className="flex justify-between mb-2">
                <Typography fontFamily={"Lota"}>Top 5 Hospitals</Typography>
                <Link
                  to={routes.hospitals.hospital}
                  className="text-[var(--c-primary-2)] font-[Lota]"
                >
                  See All{" "}
                  <ArrowForwardIcon
                    className="text-[var(--c-primary-2)]"
                    fontSize="small"
                  />
                </Link>
              </Box>

              <Box className="px-3" data-cy="dashboard-top-hospitals-table">
                {loading ? (
                  <TablePreloader />
                ) : hospitalData.length ? (
                  <Table
                    {...{
                      data: hospitalData,
                      pagination: { ...pagination, hidden: true },
                      isLoading: loading,
                    }}
                  />
                ) : (
                  <h2 className="text-center">
                    No Hospitals in the system at the moment
                  </h2>
                )}
              </Box>
            </Container>
          </div>

          <div className="w-full ">
            <Container
              className="py-5 bg-[var(--c-bg-color)] rounded-lg"
              sx={{ px: 0 }}
              data-cy="dashboard-top-hmos"
            >
              <Box className="flex justify-between mb-2">
                <Typography fontFamily={"Lota"}>Top 5 H.M.O</Typography>
                <Link
                  to={routes.hmos.hmo}
                  className="text-[var(--c-primary-2)]"
                  sx={{ fontFamily: "Lota !important" }}
                >
                  See All{" "}
                  <ArrowForwardIcon
                    className="text-[var(--c-primary-2)]"
                    fontSize="small"
                  />
                </Link>
              </Box>
              <Box className="px-3" data-cy="dashboard-top-hmos-table">
                {hmoLoading ? (
                  <TablePreloader />
                ) : hmoData.length ? (
                  <Table
                    {...{
                      data: hmoData,
                      pagination: { ...hmoPagination, hidden: true },
                      isLoading: hmoLoading,
                    }}
                  />
                ) : (
                  <h2 className="text-center">
                    No Hospitals in the system at the moment
                  </h2>
                )}
              </Box>
            </Container>
          </div>
        </Box>
      </div>
    </HomeLayout>
  );
}
